import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeader, parseResponse, normalizePhone } from '../api/helpers';
import PatientRecord from './PatientRecord';
import { inputStyle, labelStyle, modalWrap, primaryBtn, cancelBtn, successAlert, errorAlert } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.btrix.in';

function RegisterModal({ onClose, onSuccess, hospitalId }) {
  const [form, setForm]       = useState({ name: '', phone: '', age: '', gender: 'Male', dob: '', blood_group: '', address: '', condition: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const headers = await getAuthHeader();
      const res     = await axios.post(`${API_URL}/patients`, { ...form, hospital_id: hospitalId, phone: normalizePhone(form.phone) }, { headers });
      onSuccess(parseResponse(res));
    } catch { setError('Failed to register patient'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1000 }}>
      <div className="modal-box" style={{ ...modalWrap, width: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>Register New Patient</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#475569', fontSize: 20, lineHeight: 1 }}>✕</span>
        </div>
        {error && <div style={errorAlert}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Full Name</label>
              <input style={inputStyle} placeholder="Patient full name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Phone Number</label>
              <input style={inputStyle} placeholder="9000000000" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Date of Birth</label>
              <input style={{ ...inputStyle, colorScheme: 'dark' }} type="date" value={form.dob} onChange={e => setForm({...form, dob: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Age</label>
              <input style={inputStyle} type="number" placeholder="Age" value={form.age} onChange={e => setForm({...form, age: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Gender</label>
              <select style={inputStyle} value={form.gender} onChange={e => setForm({...form, gender: e.target.value})}>
                <option>Male</option><option>Female</option><option>Other</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Blood Group</label>
              <select style={inputStyle} value={form.blood_group} onChange={e => setForm({...form, blood_group: e.target.value})}>
                <option value="">— Select —</option>
                {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <option key={bg}>{bg}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Address</label>
              <input style={inputStyle} placeholder="Patient address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Condition / Reason for Visit</label>
              <input style={inputStyle} placeholder="e.g. Diabetes, General checkup" value={form.condition} onChange={e => setForm({...form, condition: e.target.value})} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, ...cancelBtn }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 2, ...primaryBtn(loading) }}>
              {loading ? 'Registering...' : 'Register Patient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Patients({ user }) {
  const isSuperAdmin                            = user?.role === 'super-admin';
  const [selectedHospital, setSelectedHospital] = useState(isSuperAdmin ? '' : user?.hospital_id);
  const [hospitals, setHospitals]               = useState([]);
  const HOSPITAL_ID                             = selectedHospital;

  const [patients, setPatients]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [viewRecord, setViewRecord]     = useState(null);
  const [successMsg, setSuccessMsg]     = useState('');

  useEffect(() => { if (isSuperAdmin) fetchHospitals(); }, []); // eslint-disable-line
  useEffect(() => { if (HOSPITAL_ID) fetchPatients(); }, [HOSPITAL_ID]); // eslint-disable-line

  const fetchHospitals = async () => {
    try {
      const headers = await getAuthHeader();
      setHospitals(parseResponse(await axios.get(`${API_URL}/hospitals`, { headers })).hospitals || []);
    } catch {}
  };

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeader();
      setPatients(parseResponse(await axios.get(`${API_URL}/patients`, { params: { hospital_id: HOSPITAL_ID }, headers })).patients || []);
    } catch {}
    finally { setLoading(false); }
  };

  const handleRegisterSuccess = (data) => {
    setShowRegister(false);
    setSuccessMsg(`Patient registered! ID: ${data.patient_id || data.patient?.patient_id}`);
    fetchPatients();
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const filtered = patients.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.phone?.includes(search) || p.patient_id?.includes(search)
  );

  const selectStyle = {
    flex: 1, padding: '9px 12px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)', fontSize: 13,
    color: 'var(--text-primary)', background: 'rgba(255,255,255,0.06)', outline: 'none',
  };

  return (
    <div>
      {isSuperAdmin && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Hospital:</div>
          <select value={selectedHospital} onChange={e => { setSelectedHospital(e.target.value); setPatients([]); }} style={selectStyle}>
            <option value="">— Select a hospital —</option>
            {hospitals.map(h => <option key={h.hospital_id} value={h.hospital_id}>{h.name} ({h.hospital_id})</option>)}
          </select>
        </div>
      )}

      {!HOSPITAL_ID && isSuperAdmin && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 14, background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)' }}>
          Select a hospital above to view patients
        </div>
      )}

      {HOSPITAL_ID && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
            <input placeholder="Search by name, phone or patient ID…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 220, padding: '9px 14px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.1)', background: 'var(--bg-input)', fontSize: 13, color: 'var(--text-primary)', outline: 'none' }} />
            {['super-admin', 'hospital-admin', 'reception'].includes(user?.role) && (
              <button onClick={() => setShowRegister(true)} style={{
                background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
                border: 'none', borderRadius: 9, padding: '9px 20px', fontSize: 13,
                fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 12px rgba(99,102,241,0.4)',
                whiteSpace: 'nowrap',
              }}>+ Register Patient</button>
            )}
          </div>

          {successMsg && <div style={successAlert}>{successMsg}</div>}

          <div className="page-card">
            {loading ? (
              <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>Loading patients…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>
                {search ? 'No patients match your search' : 'No patients registered yet'}
              </div>
            ) : (
              <div className="table-wrap">
                <table className="dark-table">
                  <thead>
                    <tr>{['Patient ID','Name','Phone','Age','Gender','Blood Group','Condition','Actions'].map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {filtered.map((p, i) => (
                      <tr key={p.patient_id || i}>
                        <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--accent-light)', fontWeight: 600 }}>{p.patient_id}</td>
                        <td style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{p.name}</td>
                        <td style={{ fontSize: 12, color: '#94a3b8' }}>{p.phone}</td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>{p.age || '—'}</td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>{p.gender || '—'}</td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>{p.blood_group || '—'}</td>
                        <td style={{ fontSize: 12, color: '#64748b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.condition || '—'}</td>
                        <td>
                          <button onClick={() => setViewRecord(p)} style={{
                            background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)',
                            borderRadius: 7, padding: '5px 12px', fontSize: 11,
                            cursor: 'pointer', color: 'var(--accent-light)', fontWeight: 600,
                          }}>📋 View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showRegister && <RegisterModal onClose={() => setShowRegister(false)} onSuccess={handleRegisterSuccess} hospitalId={HOSPITAL_ID} />}
      {viewRecord   && <PatientRecord patient={viewRecord} onClose={() => setViewRecord(null)} user={user} />}
    </div>
  );
}
